/**
 * Utility function to fix table accessibility issues in HTML content
 * @param {string} htmlContent - The HTML content to process
 * @returns {string} - The processed HTML content with accessibility fixes
 */
export function fixTableAccessibility(htmlContent) {
  if (!htmlContent) return '';

  // Create a temporary div to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Find all tables and fix accessibility issues
  const tables = tempDiv.querySelectorAll('table');
  tables.forEach((table) => {
    // Ensure table has proper structure
    if (!table.querySelector('thead') && !table.querySelector('tbody')) {
      // If table has no thead/tbody, wrap existing content
      const rows = Array.from(table.querySelectorAll('tr'));
      if (rows.length > 0) {
        // Create thead if first row contains th elements
        const firstRow = rows[0];
        const hasHeaders = firstRow.querySelectorAll('th').length > 0;

        if (hasHeaders) {
          const thead = document.createElement('thead');
          thead.appendChild(firstRow.cloneNode(true));
          table.insertBefore(thead, table.firstChild);

          // Create tbody for remaining rows
          if (rows.length > 1) {
            const tbody = document.createElement('tbody');
            for (let i = 1; i < rows.length; i++) {
              tbody.appendChild(rows[i]);
            }
            table.appendChild(tbody);
          }
        } else {
          // No headers, wrap all rows in tbody
          const tbody = document.createElement('tbody');
          rows.forEach((row) => tbody.appendChild(row));
          table.appendChild(tbody);
        }
      }
    }

    // Fix th elements that don't have associated data cells
    const thElements = table.querySelectorAll('th');
    thElements.forEach((th, index) => {
      // Add scope attribute if missing
      if (!th.getAttribute('scope')) {
        // Determine if it's a column or row header
        const parentRow = th.closest('tr');
        const isFirstRow = parentRow === table.querySelector('tr');
        th.setAttribute('scope', isFirstRow ? 'col' : 'row');
      }

      // Add id if missing for proper association
      if (!th.getAttribute('id')) {
        th.setAttribute('id', `header-${index}`);
      }
    });

    // Add headers attribute to td elements
    const tdElements = table.querySelectorAll('td');
    tdElements.forEach((td, _index) => {
      const row = td.closest('tr');
      /* eslint-disable @typescript-eslint/no-unused-vars */
      const _rowIndex = Array.from(row.parentNode.children).indexOf(row);
      const cellIndex = Array.from(row.children).indexOf(td);

      // Find corresponding header
      const thead = table.querySelector('thead');
      if (thead) {
        const headerRow = thead.querySelector('tr');
        if (headerRow && headerRow.children[cellIndex]) {
          const header = headerRow.children[cellIndex];
          if (header.tagName === 'TH' && header.getAttribute('id')) {
            td.setAttribute('headers', header.getAttribute('id'));
          }
        }
      }
    });
  });

  return tempDiv.innerHTML;
}
